import { Component } from 'react';
import { captureException, addBreadcrumb } from '../observability';

/**
 * Root error boundary (BUG-FE-04). Catches render-time errors anywhere below it and shows a
 * recoverable fallback instead of a white screen. Reports errors to Sentry for monitoring.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Add navigational context breadcrumb before reporting
    addBreadcrumb('navigation', 'error-boundary-caught', {
      route: typeof window !== 'undefined' ? window.location.pathname : undefined,
    });

    // Report to Sentry with component stack context
    captureException(error, {
      area: 'react-boundary',
      level: 'error',
      tags: {
        component_stack: errorInfo?.componentStack?.split('\n')
          .filter(l => l.trim())
          .slice(0, 5)
          .join('; ') || 'unknown',
      },
    });

    // Message only — do not log the full error object / component stack which can include props.
    console.error('Unhandled UI error:', error?.message ?? 'unknown error');
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          className="min-h-screen flex flex-col items-center justify-center gap-4 p-8 text-center"
        >
          <h1 className="text-xl font-bold text-slate-900">Something went wrong</h1>
          <p className="text-sm text-slate-600 max-w-md">
            An unexpected error occurred while rendering this page. Your session is unaffected.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
